import { useState } from 'react'

import { activityViewFor } from '../model/activityView'
import type { PortfolioOverview } from '../model/portfolioOverview'
import { ActivityTab } from './ActivityTab'
import type { DetailScope } from './detailScope'
import { HoldingsTab } from './HoldingsTab'
import { RealizedTab } from './RealizedTab'
import { type DetailTab, type DetailTabKey, TabStrip } from './TabStrip'

interface DetailTabsProps {
  scope: DetailScope
  overview: PortfolioOverview
  slug: string
}

const statementCountOf = (scope: DetailScope) =>
  scope.sections.reduce(
    (total, section) => total + section.account.statementCount,
    0,
  )

const noRealizedNote = (scope: DetailScope) =>
  statementCountOf(scope) === 1
    ? 'No realized gains found on this statement.'
    : 'No realized gains found on these statements.'

const tabsOf = (scope: DetailScope, activityCount: number): DetailTab[] => [
  {
    key: 'holdings',
    label: 'Holdings',
    count: scope.sections.reduce(
      (total, section) => total + section.holdings.length,
      0,
    ),
  },
  { key: 'activity', label: 'Activity', count: activityCount },
  ...(scope.realized.length > 0
    ? [
        {
          key: 'realized' as const,
          label: 'Realized gains',
          count: scope.realized.length,
        },
      ]
    : []),
]

// Holdings, Activity and Realized gains. Each tab draws the strip itself so Export can reach the
// rows its own filters show.
export const DetailTabs = ({ scope, overview, slug }: DetailTabsProps) => {
  const [chosenTab, setChosenTab] = useState<DetailTabKey>('holdings')
  const activityCount = activityViewFor(overview.activity, scope.baseQuery)
    .groupCounts.all
  const tabs = tabsOf(scope, activityCount)
  const tab = tabs.some((entry) => entry.key === chosenTab)
    ? chosenTab
    : 'holdings'
  const note = scope.realized.length === 0 ? noRealizedNote(scope) : undefined
  const renderStrip = (onExport: () => void) => (
    <TabStrip
      tabs={tabs}
      active={tab}
      onChange={setChosenTab}
      note={note}
      onExport={onExport}
    />
  )
  const showNames = scope.chosen == null

  if (tab === 'activity') {
    return (
      <ActivityTab
        scope={scope}
        activity={overview.activity}
        hiddenCopies={overview.hiddenCopies}
        slug={slug}
        renderStrip={renderStrip}
      />
    )
  }
  if (tab === 'realized') {
    return <RealizedTab scope={scope} slug={slug} renderStrip={renderStrip} />
  }
  return (
    <HoldingsTab
      sections={scope.sections}
      showNames={showNames}
      slug={slug}
      renderStrip={renderStrip}
    />
  )
}
